/**
 * Ver como cliente (spec 020) — chamadas do lado da EQUIPE: emitir a sessão,
 * listar e encerrar as do tenant. A aba de depuração em si fala com
 * /impersonation/current e /end (ImpersonationGate/Banner).
 */
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  IMPERSONATION_FRAGMENT_KEY,
  type ImpersonationSession,
  type ImpersonationStartResponse,
} from '@erp/shared';
import { api, apiList } from '@/lib/api-client';

export const IMPERSONATION_SESSIONS_KEY = ['erp', 'impersonation', 'sessions'] as const;
export const IMPERSONATION_PAGE_SIZE = 50;

/**
 * Endereço da aba de depuração. O token vai no FRAGMENTO (FR-007): o navegador
 * não o manda ao servidor em requisição nenhuma, nem no Referer, e a página o
 * tira da URL antes da primeira chamada.
 */
export function impersonationTabUrl(token: string): string {
  return `${window.location.origin}/portal#${IMPERSONATION_FRAGMENT_KEY}=${token}`;
}

export function useStartImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: number; reason: string }) => {
      const res = await api<ImpersonationStartResponse>(`/users/${input.userId}/impersonation`, {
        method: 'POST',
        body: { reason: input.reason },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: IMPERSONATION_SESSIONS_KEY }),
  });
}

export function impersonationSessionsQuery(params: { q: string; page: number }) {
  return queryOptions({
    queryKey: [...IMPERSONATION_SESSIONS_KEY, params] as const,
    queryFn: async ({ signal }) => {
      const res = await apiList<ImpersonationSession>('/impersonation/sessions', {
        query: {
          q: params.q || undefined,
          limit: IMPERSONATION_PAGE_SIZE,
          offset: params.page * IMPERSONATION_PAGE_SIZE,
        },
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    // a coluna "situação" e a contagem de requisições mudam sozinhas
    refetchInterval: 30_000,
  });
}

export function useEndImpersonationSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api<ImpersonationSession>(`/impersonation/sessions/${id}/end`, { method: 'POST' });
      if (res.error) throw res.error;
      return res.data;
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: IMPERSONATION_SESSIONS_KEY }),
  });
}

/** Códigos da API → frase para a tela. */
const ERROR_LABELS: Record<string, string> = {
  impersonation_target_not_client: 'Só usuários do portal podem ser vistos assim — equipe não.',
  impersonation_target_inactive: 'Este usuário está desativado: ele mesmo não entraria.',
  user_not_found: 'Usuário não encontrado nesta empresa.',
  permission_denied: 'Você não tem a permissão "Ver como cliente".',
  invalid_body: 'O motivo precisa ter de 10 a 500 caracteres.',
  impersonation_session_not_active: 'Esta sessão já tinha terminado.',
  impersonation_session_not_found: 'Sessão não encontrada.',
};

export function impersonationErrorMessage(err: unknown): string {
  const code = (err as { error?: unknown } | null)?.error;
  if (typeof code === 'string') return ERROR_LABELS[code] ?? code;
  return err instanceof Error ? err.message : 'Erro inesperado';
}
